import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { Button, MenuItem, Stack, TextField } from "@mui/material";

import { HOME_TOKENS } from "./tokens";

export interface IPanelFilterState {
  search: string;
  status: string;
}

export interface IPanelFilterOption {
  /** `""` é a opção "Todos" — mesma convenção de `allValue` abaixo. */
  value: string;
  label: string;
  count: number;
}

interface IPanelFilterProps {
  filter: IPanelFilterState;
  onChangeFilter: Dispatch<SetStateAction<IPanelFilterState>>;
  onClear: () => void;
  statusOptions: IPanelFilterOption[];
  /** Valor de `status` que representa "sem filtro de situação". Default `""`. */
  allValue?: string;
}

/**
 * Barra de filtro da tela inicial (T8). Copia o padrão já praticado em
 * `src/pages/operation/customer/list/filter/index.tsx` — `TextField select
 * size="small"`, `Button variant="contained" color="primary" size="small"`
 * desabilitado sem filtro, container com `justify-content: space-between` e
 * campos agrupados à esquerda.
 *
 * Duas diferenças deliberadas em relação à referência:
 *   1. Cada `MenuItem` mostra sua contagem — abrir o menu já responde "quantos
 *      há em cada grupo" sem precisar filtrar.
 *   2. A busca acha CNPJ formatado (`matchesPanelSearch` abaixo), não só nome.
 */
export function PanelFilter({ filter, onChangeFilter, onClear, statusOptions, allValue = "" }: IPanelFilterProps) {
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter((prev) => ({ ...prev, search: event.target.value }));
  };

  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter((prev) => ({ ...prev, status: event.target.value }));
  };

  const isFiltered = filter.search !== "" || filter.status !== allValue;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" sx={{ gap: `${HOME_TOKENS.filter.gap}px` }}>
        <TextField
          size="small"
          label="Pesquisar"
          placeholder="Pesquisar por nome ou CNPJ"
          type="text"
          variant="outlined"
          value={filter.search}
          onChange={handleSearchChange}
          sx={{ width: `${HOME_TOKENS.filter.fieldWidth}px` }}
        />

        <TextField
          select
          size="small"
          label="Situação"
          value={filter.status}
          onChange={handleStatusChange}
          sx={{ width: `${HOME_TOKENS.filter.fieldWidth}px` }}
        >
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label} ({option.count})
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={onClear}
        disabled={!isFiltered}
        sx={{ height: `${HOME_TOKENS.filter.controlHeight}px`, px: 2 }}
      >
        Limpar Filtro
      </Button>
    </Stack>
  );
}

/**
 * Diferença #2 da barra em relação à referência copiada: `term` pode chegar
 * formatado (`48.785.828/0093-47`) porque é o que aparece na tela — strip de
 * não-dígito antes de comparar contra o CNPJ cru do contrato.
 */
export function matchesPanelSearch(term: string, candidate: { buyerName: string; federalTaxId: string }): boolean {
  const trimmed = term.trim();
  if (!trimmed) return true;

  if (candidate.buyerName.toLowerCase().includes(trimmed.toLowerCase())) return true;

  const digits = trimmed.replace(/\D/g, "");
  return digits.length > 0 && candidate.federalTaxId.includes(digits);
}
